import type { CompanyProfile } from '@/lib/companies'
import type { JobCard as JobCardData, PageWithLayout } from '@/types'

import { useState } from 'react'
import Link from 'next/link'

import {
    ArrowRight,
    BriefcaseBusiness,
    ChevronRight,
    ExternalLink,
    Globe,
    Laptop,
    MapPin,
    Rss
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { withPage } from '@/lib/server/page'
import { formatDate } from '@/lib/format'
import { getCompanyBySlug } from '@/lib/companies'
import { searchJobs } from '@/lib/jobs/queries'
import { DEFAULT_FILTERS } from '@/lib/jobs/filters'
import { boardUrl, providerLabel } from '@/lib/ingest/sources'
import { scoreJobCards } from '@/lib/match/score-cards'
import { Seo } from '@/components/generals/seo'
import { publicLayout } from '@/components/generals/page-layouts'
import { CompanyLogo } from '@/components/generals/company-logo'
import { EmptyState } from '@/components/generals/states'
import { JobCard } from '@/components/jobs/job-card'
import { ButtonLink } from '@/components/ui/button'

import { categoryLabel } from '@/constants/taxonomy'

interface CompanyPageProps {
    company: CompanyProfile
    jobs: Array<JobCardData>
}

export const getServerSideProps = withPage<CompanyPageProps>(async (context, { viewer }) => {
    const company = await getCompanyBySlug(String(context.params?.slug ?? ''))
    if (!company) return { notFound: true }

    const results = await searchJobs({ ...DEFAULT_FILTERS, companies: [company.slug] }, viewer?.id ?? null, 60)
    const jobs = viewer?.primaryResumeId
        ? await scoreJobCards(results.items, viewer.id, viewer.primaryResumeId)
        : results.items

    return { props: { company, jobs } }
})

const CompanyPage: PageWithLayout<CompanyPageProps> = ({ company, jobs }) => {
    const [category, setCategory] = useState('all')
    const visible = category === 'all' ? jobs : jobs.filter(job => job.category === category)
    const remoteShare = company.openJobs > 0 ? Math.round((company.remoteJobs / company.openJobs) * 100) : 0

    return (
        <>
            <Seo
                title={`${company.name} jobs`}
                path={`/companies/${company.slug}`}
                description={`${company.openJobs} open technology roles at ${company.name}, collected from their career page. ${company.tagline ?? ''}`.trim()}
            />

            <section className='border-b border-border/70 bg-background'>
                <div className='container-page py-8'>
                    <nav aria-label='Breadcrumb' className='mb-6 flex items-center gap-1.5 text-sm text-muted-foreground'>
                        <Link href='/companies' className='hover:text-foreground'>Companies</Link>
                        <ChevronRight className='size-3.5' />
                        <span className='text-foreground' data-mdlfy-cms={cms('companies', company.id, 'name')}>{company.name}</span>
                    </nav>

                    <div className='flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between'>
                        <div className='flex items-start gap-5'>
                            <CompanyLogo name={company.name} logo={company.logo} logoAlt={company.logoAlt} size='xl' />
                            <div className='flex max-w-2xl flex-col gap-2'>
                                <h1 className='text-3xl font-bold'>
                                    <span data-mdlfy-cms={cms('companies', company.id, 'name')}>{company.name}</span>
                                </h1>
                                {company.tagline && <p className='text-lg text-foreground/80' data-mdlfy-cms={cms('companies', company.id, 'tagline')}>{company.tagline}</p>}
                                {company.description && <p className='text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={cms('companies', company.id, 'description')}>{company.description}</p>}
                                <div className='mt-1 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground'>
                                    {company.headquarters && (
                                        <span className='flex items-center gap-1.5'>
                                            <MapPin className='size-4' />
                                            <span data-mdlfy-cms={cms('companies', company.id, 'headquarters')}>{company.headquarters}</span>
                                        </span>
                                    )}
                                    {company.industry && (
                                        <span className='flex items-center gap-1.5'>
                                            <BriefcaseBusiness className='size-4' />
                                            <span data-mdlfy-cms={cms('companies', company.id, 'industry')}>{company.industry}</span>
                                        </span>
                                    )}
                                    {company.website && (
                                        <a href={company.website} target='_blank' rel='noopener noreferrer' className='flex items-center gap-1.5 text-primary hover:underline'>
                                            <Globe className='size-4' />
                                            Website
                                        </a>
                                    )}
                                    {company.careersUrl && (
                                        <a href={company.careersUrl} target='_blank' rel='noopener noreferrer' className='flex items-center gap-1.5 text-primary hover:underline'>
                                            <ExternalLink className='size-4' />
                                            Careers page
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        <dl className='grid grid-cols-3 gap-3 lg:w-96'>
                            <div className='rounded-2xl border border-border/80 bg-card p-4'>
                                <dt className='text-xs text-muted-foreground'>Open roles</dt>
                                <dd className='mt-1 text-2xl font-bold'>{company.openJobs}</dd>
                            </div>
                            <div className='rounded-2xl border border-border/80 bg-card p-4'>
                                <dt className='text-xs text-muted-foreground'>Remote</dt>
                                <dd className='mt-1 text-2xl font-bold'>{remoteShare}%</dd>
                            </div>
                            <div className='rounded-2xl border border-border/80 bg-card p-4'>
                                <dt className='text-xs text-muted-foreground'>Countries</dt>
                                <dd className='mt-1 text-2xl font-bold'>{company.countries.length}</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </section>

            <div className='container-page grid gap-8 py-8 lg:grid-cols-[minmax(0,1fr)_20rem]'>
                <section aria-labelledby='open-roles' className='flex min-w-0 flex-col gap-4'>
                    <div className='flex flex-wrap items-center justify-between gap-3'>
                        <h2 id='open-roles' className='text-xl font-semibold'>Open roles</h2>
                        {company.openJobs > jobs.length && (
                            <ButtonLink href={`/jobs?company=${company.slug}`} variant='outline' size='sm'>
                                Filter all {company.openJobs} roles
                                <ArrowRight />
                            </ButtonLink>
                        )}
                    </div>

                    {company.categories.length > 1 && (
                        <div className='flex flex-wrap gap-2' role='group' aria-label='Filter by category'>
                            {[{ category: 'all', count: jobs.length }, ...company.categories].map(item => (
                                <button
                                    key={item.category}
                                    type='button'
                                    onClick={() => setCategory(item.category)}
                                    aria-pressed={category === item.category}
                                    className={cn(
                                        'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                                        category === item.category ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:border-primary/40'
                                    )}
                                >
                                    {item.category === 'all' ? 'All' : categoryLabel(item.category)}
                                    <span className='ml-1.5 opacity-70'>{item.count}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {visible.length === 0 ? (
                        <EmptyState
                            icon={BriefcaseBusiness}
                            title={jobs.length === 0 ? `${company.name} has no open tech roles right now` : 'No roles in this category on this page'}
                            description={jobs.length === 0 ? 'We check their career page every day. Create an alert from the jobs page to hear when something opens up.' : 'Use the full filter to see every role in this category.'}
                            action={<ButtonLink href={`/jobs?company=${company.slug}`} variant='outline'>Open job search</ButtonLink>}
                        />
                    ) : (
                        <div className='flex flex-col gap-3'>
                            {visible.map(job => <JobCard key={job.id} job={job} />)}
                        </div>
                    )}
                </section>

                <aside className='flex flex-col gap-5'>
                    {company.categories.length > 0 && (
                        <div className='rounded-2xl border border-border/80 bg-card p-5 shadow-card'>
                            <h2 className='mb-3 text-sm font-semibold'>Hiring by category</h2>
                            <ul className='flex flex-col gap-3'>
                                {company.categories.map(item => (
                                    <li key={item.category} className='flex flex-col gap-1.5'>
                                        <div className='flex justify-between text-sm'>
                                            <span>{categoryLabel(item.category)}</span>
                                            <span className='font-semibold tabular-nums'>{item.count}</span>
                                        </div>
                                        <div className='h-1.5 overflow-hidden rounded-full bg-muted'>
                                            <div className='h-full rounded-full bg-primary' style={{ width: `${Math.max(4, (item.count / company.openJobs) * 100)}%` }} />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {company.countries.length > 0 && (
                        <div className='rounded-2xl border border-border/80 bg-card p-5 shadow-card'>
                            <h2 className='mb-3 text-sm font-semibold'>Where they hire</h2>
                            <ul className='flex flex-col gap-2 text-sm'>
                                {company.countries.map(item => (
                                    <li key={item.country} className='flex justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <MapPin className='size-3.5 text-muted-foreground' />
                                            {item.country}
                                        </span>
                                        <span className='text-muted-foreground tabular-nums'>{item.count}</span>
                                    </li>
                                ))}
                                {company.remoteJobs > 0 && (
                                    <li className='flex justify-between'>
                                        <span className='flex items-center gap-2'>
                                            <Laptop className='size-3.5 text-muted-foreground' />
                                            Remote
                                        </span>
                                        <span className='text-muted-foreground tabular-nums'>{company.remoteJobs}</span>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    {company.sources.length > 0 && (
                        <div className='rounded-2xl border border-border/80 bg-card p-5 shadow-card'>
                            <h2 className='mb-3 text-sm font-semibold'>Where these jobs come from</h2>
                            <ul className='flex flex-col gap-3 text-sm'>
                                {company.sources.map(source => (
                                    <li key={`${source.provider}-${source.boardToken}`} className='flex items-start gap-2.5'>
                                        <Rss className='mt-0.5 size-4 text-muted-foreground' />
                                        <div>
                                            <a href={boardUrl(source.provider, source.boardToken)} target='_blank' rel='noopener noreferrer' className='font-medium text-primary hover:underline'>
                                                {company.name} on {providerLabel(source.provider)}
                                            </a>
                                            {source.lastRunAt && <p className='text-xs text-muted-foreground'>Last checked {formatDate(source.lastRunAt)}</p>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>
            </div>
        </>
    )
}

CompanyPage.getLayout = publicLayout

export default CompanyPage
